import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
} from '@mui/material';
import { IconPlus, IconEdit, IconTrash, IconCheck, IconX } from '@tabler/icons-react';
import {
  fetchScheduledReports,
  fetchReportDefinitions,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
} from '../../slices/reportsSlice';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'يومي' },
  { value: 'weekly', label: 'أسبوعي' },
  { value: 'monthly', label: 'شهري' },
  { value: 'yearly', label: 'سنوي' },
];

const ScheduledReportsTab = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { scheduledReports, reportDefinitions, scheduledLoading } = useSelector(
    (state) => state.reports
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [formData, setFormData] = useState({
    report_definition: '',
    frequency: 'monthly',
    next_run: '',
    recipients: [],
    is_active: true,
  });

  useEffect(() => {
    dispatch(fetchScheduledReports());
    dispatch(fetchReportDefinitions());
  }, [dispatch]);

  const handleOpenDialog = (report = null) => {
    if (report) {
      setEditingReport(report);
      setFormData({
        report_definition: report.report_definition,
        frequency: report.frequency,
        next_run: report.next_run?.split('T')[0] || '',
        recipients: report.recipients || [],
        is_active: report.is_active,
      });
    } else {
      setEditingReport(null);
      setFormData({
        report_definition: '',
        frequency: 'monthly',
        next_run: '',
        recipients: [],
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingReport(null);
  };

  const handleSave = async () => {
    if (!formData.report_definition) {
      showToast('يرجى اختيار تقرير', 'error');
      return;
    }
    if (!formData.next_run) {
      showToast('يرجى تحديد الموعد القادم', 'error');
      return;
    }

    try {
      const data = {
        ...formData,
        next_run: new Date(formData.next_run).toISOString(),
      };

      if (editingReport) {
        await dispatch(
          updateScheduledReport({ id: editingReport.id, data })
        ).unwrap();
        showToast('تم تحديث التقرير المجدول بنجاح', 'success');
      } else {
        await dispatch(createScheduledReport(data)).unwrap();
        showToast('تم إنشاء التقرير المجدول بنجاح', 'success');
      }
      handleCloseDialog();
      dispatch(fetchScheduledReports());
    } catch (error) {
      showToast(error.message || 'حدث خطأ', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا التقرير المجدول؟')) {
      try {
        await dispatch(deleteScheduledReport(id)).unwrap();
        showToast('تم حذف التقرير المجدول بنجاح', 'success');
        dispatch(fetchScheduledReports());
      } catch (error) {
        showToast(error.message || 'حدث خطأ', 'error');
      }
    }
  };

  const getFrequencyLabel = (frequency) => {
    const option = FREQUENCY_OPTIONS.find((opt) => opt.value === frequency);
    return option?.label || frequency;
  };

  if (scheduledLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">التقارير المجدولة</Typography>
        <Button
          variant="contained"
          startIcon={<IconPlus />}
          onClick={() => handleOpenDialog()}
        >
          إضافة تقرير مجدول
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>اسم التقرير</TableCell>
              <TableCell>التكرار</TableCell>
              <TableCell>الموعد القادم</TableCell>
              <TableCell>آخر تشغيل</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scheduledReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  لا توجد تقارير مجدولة
                </TableCell>
              </TableRow>
            ) : (
              scheduledReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.report_definition_name}</TableCell>
                  <TableCell>{getFrequencyLabel(report.frequency)}</TableCell>
                  <TableCell>
                    {new Date(report.next_run).toLocaleDateString('en-GB')}
                  </TableCell>
                  <TableCell>
                    {report.last_run
                      ? new Date(report.last_run).toLocaleDateString('en-GB')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={report.is_active ? 'نشط' : 'غير نشط'}
                      color={report.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(report)}
                    >
                      <IconEdit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(report.id)}
                      color="error"
                    >
                      <IconTrash />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingReport ? 'تعديل تقرير مجدول' : 'إضافة تقرير مجدول'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>التقرير</InputLabel>
              <Select
                value={formData.report_definition}
                label="التقرير"
                onChange={(e) =>
                  setFormData({ ...formData, report_definition: e.target.value })
                }
              >
                {reportDefinitions.map((def) => (
                  <MenuItem key={def.id} value={def.id}>
                    {def.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>التكرار</InputLabel>
              <Select
                value={formData.frequency}
                label="التكرار"
                onChange={(e) =>
                  setFormData({ ...formData, frequency: e.target.value })
                }
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="الموعد القادم"
              type="datetime-local"
              value={formData.next_run}
              onChange={(e) =>
                setFormData({ ...formData, next_run: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<IconX />}>
            إلغاء
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<IconCheck />}
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledReportsTab;
